import { type FC, type ReactElement, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { FormattedMessage } from 'dibs-react-intl';
import { type HeadingType } from 'dibs-controlled-heading/exports/HeadingLevel.tsx';

import { pageTypeConstants as pageTypes } from '../../constants/pageTypeConstants.ts';
import { FILTER_SEARCH_TYPE, SEARCH_TYPES } from '../../constants/sbConstants.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { getFilterValueByUrlLabel } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';
import { useSharedSaveSearchContext } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';
import {
    checkRewardedSeller,
    checkTradeUserUnderTopLevelLoyaltyTiers,
} from 'dibs-buyer-layout/exports/sellerBrandingHelpers.ts';
import { totalResultsMessage } from '../sbMessages.tsx';

import { type SbSharedTitle_itemSearch$key } from './__generated__/SbSharedTitle_itemSearch.graphql.ts';
import { type SbSharedTitle_user$key } from './__generated__/SbSharedTitle_user.graphql.ts';

import styles from './SbSharedTitle.scss';

const { COLLECTION, CREATOR, SEARCH, DESIGN, DEALER, MORE_FROM_SELLER } = pageTypes;
const { FUZZY } = SEARCH_TYPES;

/* eslint-disable relay/unused-fields -- appliedFilters read via getFilterValueByUrlLabel */
const itemSearchFragment = graphql`
    fragment SbSharedTitle_itemSearch on ItemSearchQueryConnection {
        pageType
        searchTerm
        totalResults
        searchCorrections {
            originalSearchTerm
        }
        meta {
            header
            subHeader
        }
        appliedFilters {
            name
            values {
                urlLabel
            }
        }
        soldItems: soldResults(first: 28) {
            totalResults
        }
        seller {
            ...sellerBrandingHelpers_seller
        }
        browseContentModule(contentModuleId: $contentModuleId) {
            ... on ContentModuleBannerTiles {
                banners {
                    __typename
                }
            }
        }
        searchResultData {
            vectorResultCount
        }
    }
`;
/* eslint-enable relay/unused-fields */

const userFragment = graphql`
    fragment SbSharedTitle_user on User {
        ...sellerBrandingHelpers_user
    }
`;

type Props = {
    itemSearch: SbSharedTitle_itemSearch$key;
    user: SbSharedTitle_user$key | null | undefined;
    Heading?: HeadingType;
    showContentModule?: boolean;
    children?: ReactNode | ReactNode[];
    headerWrapperClass?: string;
};

export const SbSharedTitle: FC<Props> = ({
    itemSearch: itemSearchRef,
    user: userRef,
    Heading,
    showContentModule = false,
    children,
    headerWrapperClass,
}) => {
    const { showFollowButton } = useSharedSaveSearchContext();
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);
    const isClient = useSbSelector(state => state.relayVariables.variables.isClient);
    const { isMobile, isQuickFiltersRedesignVariant, isRemoveCountsVariant } =
        useServerVarsContext();
    const hasVectorSearchResults = !!itemSearch?.searchResultData?.vectorResultCount;

    const {
        browseContentModule,
        meta,
        pageType,
        appliedFilters,
        searchCorrections,
        searchTerm,
        soldItems,
        totalResults,
        seller,
    } = itemSearch;
    const { header, subHeader } = meta || {};
    const { originalSearchTerm } = searchCorrections || {};
    const isNullResultPage = totalResults === 0;
    const isFuzzyResults = !!getFilterValueByUrlLabel(appliedFilters, FILTER_SEARCH_TYPE, FUZZY);
    const showTitleFollowButton = showFollowButton && !isFuzzyResults;

    const hasSpellCorrection = originalSearchTerm;
    const HeadingTag = pageType === DESIGN && showContentModule ? 'h2' : 'h1';

    let customHeader: ReactNode;
    if (hasSpellCorrection) {
        customHeader = (
            <FormattedMessage
                id="sb.SbSharedTitle.spellTitle"
                defaultMessage="Showing results for"
            />
        );
    } else if (isMobile) {
        customHeader = (
            <FormattedMessage id="sb.SbSharedTitle.title" defaultMessage="Results for" />
        );
    } else if (isNullResultPage || isFuzzyResults) {
        customHeader = (
            <FormattedMessage
                id="sb.SbSharedTitle.fuzzyResultsTitle"
                defaultMessage="We didn't find any results for"
            />
        );
    }

    const HeaderTitle = (): ReactElement => {
        if (pageType === SEARCH) {
            const headerWithoutCounts =
                isRemoveCountsVariant || hasVectorSearchResults ? (
                    <FormattedMessage id="sb.SbSharedTitle.title" defaultMessage="Results for" />
                ) : null;
            return (
                <span>
                    {customHeader || headerWithoutCounts || header}{' '}
                    <i className={dibsCss.sassyFontHeaderNoneItalic} data-tn="search-term">
                        "{searchTerm}"
                    </i>
                </span>
            );
        }
        if (pageType === DEALER) {
            if (isClient) {
                const shouldShowSellerName =
                    checkRewardedSeller(seller) ||
                    checkTradeUserUnderTopLevelLoyaltyTiers(user || null);

                if (!shouldShowSellerName) {
                    return (
                        <FormattedMessage
                            id="sb.SbSharedTitle.hiddenSellerName"
                            defaultMessage="Vetted Professional Seller"
                        />
                    );
                }
            } else {
                return (
                    <span className={classnames(styles.shimmer, dibsCss.textTransparent)}>
                        {header}
                    </span>
                );
            }
        }
        return <>{header}</>;
    };

    const wrapperClass = classnames(dibsCss.flex, dibsCss.itemsCenter, dibsCss.m0, {
        [dibsCss.mtSmall]: !isQuickFiltersRedesignVariant,
        [dibsCss.mtMedium]: isQuickFiltersRedesignVariant,
    });
    const headerWrapperClasses = classnames(wrapperClass, headerWrapperClass);
    const subHeaderWrapperClasses = classnames(isFuzzyResults && wrapperClass);
    const subHeaderClass = classnames(styles.subHeader, isFuzzyResults && dibsCss.mrXsmall);

    if (pageType === COLLECTION && browseContentModule?.banners) {
        return null;
    }

    return (
        <div className={classnames(styles.container, dibsCss.flex, dibsCss.flexCol)}>
            <div className={headerWrapperClasses}>
                <HeadingTag
                    className={classnames(styles.header, dibsCss.m0)}
                    data-tn="header-title"
                >
                    <HeaderTitle />
                </HeadingTag>
                {showTitleFollowButton && children}
                {isQuickFiltersRedesignVariant &&
                    !isRemoveCountsVariant &&
                    !hasVectorSearchResults &&
                    !!totalResults &&
                    pageType !== SEARCH &&
                    pageType !== CREATOR &&
                    pageType !== MORE_FROM_SELLER && (
                        <div
                            className={classnames(
                                styles.totalResults,
                                dibsCss.mlXsmall,
                                dibsCss.whitespaceNowrap,
                                dibsCss.textSatan,
                                dibsCss.textBodySmallNormal
                            )}
                        >
                            {totalResultsMessage(totalResults)}
                        </div>
                    )}
            </div>
            {subHeader && Heading && !(isMobile && isFuzzyResults) && (
                <div className={subHeaderWrapperClasses}>
                    <Heading className={subHeaderClass} data-tn="header-sub-title">
                        {subHeader}
                    </Heading>
                    {isFuzzyResults && children}
                </div>
            )}
            {isFuzzyResults && !soldItems?.totalResults && !!totalResults && (
                <div
                    className={classnames(
                        styles.fewerResultsHeader,
                        dibsCss.mtMedium,
                        dibsCss.ptSmall,
                        dibsCss.borderT,
                        dibsCss.borderTSolid,
                        dibsCss.borderTMoonstone
                    )}
                >
                    <FormattedMessage
                        id="sb.SbSharedTitle.matchingFewerWordsTitle"
                        defaultMessage="Showing results matching fewer words"
                    />
                </div>
            )}
        </div>
    );
};
